import { CircularProgress } from '@mui/material';
import { shallowEqual, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import DateFormat from 'src/components/DateFormat';
import { FinalBillPrintAdmin } from 'src/components/bill-print/adminPaymentBill';
import Button from 'src/components/button';
import Card from 'src/components/card';
import useFetch from 'src/hooks/useFetch';
import { RootState } from 'src/redux/reducers';
import { formatDateTime } from '../kds';
import { CurrencyAmount } from 'src/containers/currency';
import { PaymentStatusButton } from 'src/components/payment-status';
import { getDiscountStorageValue } from 'src/components/discount-storage';

interface Props {
  orderId?: string;
}

const SingleOrder = ({orderId}: Props) => {
  const { id: paramsId } = useParams();
  const id = orderId ?? paramsId;
  const navigate = useNavigate();
  const { data: orderData, isLoading } = useFetch(`/payments/receipt/${id}`);
  const {
    customer,
    employee = {
      name: '',
    },
    createdAt,
    items = [],
  } = orderData || {};
  const { settingData } = useSelector(
    ({ settingData }: RootState) => settingData,
    shallowEqual
  );
  const businessName = settingData?.businessInfo?.name;
  const businessAddress = settingData?.businessInfo?.address;
  const panNumber = settingData?.businessInfo?.pan;
  const discountSend = {
    discountsWhole: orderData?.customDiscountInfo?.amount ?? null,
    individualItemAmount: orderData?.customDiscountInfo?.individualItemAmount,
  };
  const totalExtraCharges = orderData?.extraCharges?.reduce(
    (sum: number, item: any) => sum + Number(item.amount || 0),
    0
  ) ?? 0;
  const orderInfo = {
    total: orderData?.total,
    subTotal: orderData?.subTotal,
    taxTotal: totalExtraCharges
  };
  const totalAmountRecieved = orderData?.total - orderData?.remaining;
  const userInfo = JSON.parse(window.localStorage.getItem('userInfo') || '');
  const allowOrderCharge = userInfo?.userInfo?.permissions?.allowOrderCharge ?? false;  

  const headingContent: any = (
    <div className="flex items-center gap-3">
      <p className="text-black text-xl font-bold">{`#${orderData?.referenceNumber}`}</p>
      <p
        className={`${
          orderData?.orderType === 'dine_in'
            ? 'border border-[#FEC02D] text-[#FEC02D]'
            : orderData?.orderType === 'delivery'
            ? 'border border-success text-success'
            : orderData?.orderType === 'takeaway'
            ? 'border border-warning text-warning'
            : ''
        } text-sm w-[135px] rounded capitalize py-2 text-center`}
      >
        {orderData?.orderType}
      </p>
      {PaymentStatusButton(orderData?.status, 'text-sm w-[135px] rounded capitalize py-2 text-center')}
    </div>
  );

  const handlePrintFinalBill = () => {
    const prtContent = document.getElementById(`finalBillPrintAdmin`);
    if (!prtContent) return;
    const printWindow = window.open(
      '',
      '',
      'left=0,top=0,width=800,height=900,toolbar=0,scrollbars=0,status=0'
    );
    if (printWindow) {
      printWindow.document.write(prtContent.innerHTML);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    } else {
      console.error('Failed to open a new window for printing.');
    }
  };
  const handleMakePayment = () => {
    const url = orderId ? `/payment/admin/tables` : `/payment/admin`;
    if(allowOrderCharge) navigate(url, { state: { order: orderData } });
  };
  if (isLoading)
    return (
      <div className="flex justify-center mt-10">
        <CircularProgress />
      </div>
    );
  return (
    <>
      <div className="flex mb-4 text-black">
        <p>{headingContent}</p>
      </div>
      <div className="flex items-center justify-between">
        <p className="text-secondary-0 text-base">
          {orderData && <DateFormat text={createdAt} />}
        </p>
        <div className="flex gap-2">
          <Button
            text={'Print Bill'}
            className="ml-auto"
            onClick={handlePrintFinalBill}
          />
          <div className="relative group ml-auto">
            <div>
              <Button
                text={'Make Payment'}
                className={`${
                  allowOrderCharge
                    ? 'cursor-pointer'
                    : 'cursor-not-allowed opacity-50'
                }`}
                onClick={handleMakePayment}
                disabled={!allowOrderCharge}
              />
            </div>
            {!allowOrderCharge && (
                <div className="absolute bottom-full mb-2 right-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-black text-pure-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                  Insufficient permissions to charge order
                </div>
              )}
          </div>
        </div>
      </div>
      <div className="flex flex-col xl:flex-row gap-4 xl:gap-0 justify-between text-[black] xl:space-x-8 mt-6 ">
        {/* left */}
        <div className="flex flex-col">
          <Card className="w-full xl:w-[420px] h-fit  rounded-b-none px-2">
            <div title="" className="">
              {items?.map((item: any) => {
                return (
                  <div key={item.itemId}>
                    <div className="flex justify-between py-2 px-4">
                      <div className="flex gap-2">
                        <p className="font-semibold text-base">
                          {item.itemName}{' '}
                          {item.hasVariants ? `(${item.variantName})` : ''}
                        </p>
                        <p className="text-base">x{item.quantity}</p>
                      </div>
                      <p className="text-base font-normal ">
                        <CurrencyAmount amount={item.itemTotal}/>
                      </p>
                    </div>
                    {item.modifiers.map((modifier: any) => {
                      return (
                        <div key={modifier.id}>
                          <ul>
                            {modifier.options.map((option: any) => {
                              return (
                                <li key={option.id}>
                                  <div className="flex justify-between py-2 px-4 text-[#949CC0]">
                                    <p>{option.name}</p>
                                    <p>{option.price}</p>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </Card>
          <div className="rounded-xl rounded-t-none  border border-border-color border-t-0 p-4 bg-pure-white ">
            <div className="flex justify-between  py-1 px-2">
              <p className=" text-secondary-0">Subtotal</p>
              <p className=" "><CurrencyAmount amount={orderData?.subTotal}/></p>
            </div>
            {orderData?.customDiscountInfo?.individualItemAmount > 0 && (
              <div className="flex justify-between  py-1 px-2">
                <p className=" text-secondary-0">Itemized Discount</p>
                <p><CurrencyAmount amount={orderData?.customDiscountInfo?.individualItemAmount}/></p>
              </div>
            )}
            {orderData?.customDiscountInfo?.amount > 0 && (
              <div className="flex justify-between  py-1 px-2">
                <p className=" text-secondary-0">Applied Discount</p>
                <p><CurrencyAmount amount={orderData?.customDiscountInfo?.amount}/></p>
              </div>
            )}
            <hr className='text-primary-light'/>
            {orderData?.taxTotal > 0 && (
              <div className="flex justify-between  py-1 px-2">
                <p className=" text-secondary-0">Tax</p>
                <p className=""><CurrencyAmount amount={orderData?.taxTotal}/></p>
              </div>
            )}
            <div className="flex justify-between  py-1 px-2">
              <p className=" text-secondary-0">Total</p>
              <p className="font-bold "><CurrencyAmount amount={orderData?.total}/></p>
            </div>
          </div>
        </div>
        {/* right */}
        <Card className="flex flex-col w-full">
          {/* top */}
          <div className="flex flex-1 space-x-10">
            <div className="w-full">
              <div className="mb-6 flex flex-col gap-4">
                <h1 className="text-light-grey text-base font-semibold mb-2 ">
                  Customer Details
                </h1>
                <div className="w-full flex flex-col gap-3 text-base">
                  <div className="grid grid-cols-6">
                    <p className="col-span-1 text-secondary-0">Name</p>
                    <p className="col-span-5">{customer?.name ?? '-'}</p>
                  </div>
                  <div className="grid grid-cols-6">
                    <p className="col-span-1 text-secondary-0">Email</p>
                    <p className="col-span-5">{customer?.email ?? '-'}</p>
                  </div>
                  <div className="grid grid-cols-6">
                    <p className="col-span-1 text-secondary-0">Phone</p>
                    <p className="col-span-5">{customer?.phone ?? '-'}</p>
                  </div>
                </div>
                <Button
                  disabled={customer === null || paramsId === undefined}
                  onClick={() => navigate(`/customer-detail/${customer?.id}`)}
                  text={'View Profile'}
                  className={`mt-2 w-fit text-primary bg-[#D9E0FF] ring-[#D9E0FF]`}
                />
              </div>
            </div>
            <div className="w-full flex flex-col gap-4">
              <h1 className="text-light-grey text-base font-semibold mb-2 ">
                Order Info
              </h1>
              <div className="w-full flex flex-col gap-3 text-base">
                <div className="grid grid-cols-6">
                  <p className="col-span-1 text-secondary-0">Source</p>
                  <p className="col-span-5">
                    {orderData?.origin
                      ?.split('_')
                      ?.join(' ')
                      ?.toString()
                      ?.toUpperCase()}
                  </p>
                </div>
                <div className="grid grid-cols-6">
                  <p className="col-span-1 text-secondary-0">Origin</p>
                  <p className="col-span-5">
                    {orderData?.orderType
                      ?.split('_')
                      ?.join(' ')
                      ?.toString()
                      ?.toUpperCase()}
                  </p>
                </div>
                <div className="grid grid-cols-6">
                  <p className="col-span-1 text-secondary-0">Table</p>
                  <p className="col-span-5">{orderData?.table?.name}</p>
                </div>
                <div className="grid grid-cols-6">
                  <p className="col-span-1 text-secondary-0">Cashier</p>
                  <p className="col-span-5">{employee?.name}</p>
                </div>
              </div>
            </div>
          </div>
          {/* bottom */}
          <div className="flex flex-col gap-4 w-[650px]">
            <h1 className="text-light-grey text-base font-semibold mb-2 ">
              Payment Info
            </h1>
            <div className="w-full flex flex-col gap-3 text-base">
              {orderData?.transactions?.length === 1 ? (
                <div className="grid grid-cols-6">
                  <p className="col-span-1 text-secondary-0">Method</p>
                  <p className="col-span-5">
                    {' '}
                    {orderData?.transactions
                      ?.map((x: any) => x.paymentMethod)
                      .join(', ')}
                    {orderData?.status === 'not_paid' && 'Full credit'}
                  </p>
                </div>
              ) : null}
              {orderData?.transactions?.length > 1 ? (
                <div className="grid grid-cols-6">
                  <p className="col-span-1 text-secondary-0">Methods</p>
                  <p className="col-span-5 border border-solid border-light-grey p-1 rounded-md">
                    <div className="grid grid-cols-5 text-light-grey mb-1 border-solid border-0 border-b-[1px]">
                      <span className="col-span-2">Paid at</span>
                      <span className="col-span-2">Method</span>
                      <span className="col-span-1">Amount</span>
                    </div>
                    {orderData?.transactions?.map((x: any, i: number) => (
                      <div className="grid grid-cols-5">
                        <span className="col-span-2">
                          {formatDateTime(x.createdAt)}
                        </span>
                        <span className="col-span-2">{`${x.paymentMethod}`}</span>
                        <span className="col-span-1"><CurrencyAmount amount={x.amount}/></span>
                      </div>
                    ))}
                  </p>
                </div>
              ) : null}
              <div className="grid grid-cols-6">
                <p className="col-span-1 text-secondary-0">Paid</p>
                <p className="col-span-5">
                  <CurrencyAmount amount={orderData?.total - orderData?.remaining}/>
                </p>
              </div>
              {orderData?.remaining > 0 && (
                <div className="grid grid-cols-6">
                  <p className="col-span-1 text-secondary-0">Credit</p>
                  <p className="col-span-5"><CurrencyAmount amount={orderData?.remaining}/></p>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>
      {FinalBillPrintAdmin(
        orderData,
        businessName,
        businessAddress,
        panNumber,
        orderData?.remaining,
        orderInfo,
        customer,
        discountSend,
        orderData?.status === 'not_paid'
          ? [{ paymentMethod: 'Full credit' }]
          : orderData?.transactions,
        totalAmountRecieved
      )}
    </>
  );
};

export default SingleOrder;
